import * as React from 'react'
import cx from 'classnames'

import EditorContent, {
  SliceFC,
  SlicesOrderComparator,
} from 'components/RichTextEditor/EditorContent'

import { SliceType } from '../MCTextEditor/slice/types'

import ContentEditableInput from './contentEditable/EditorInput'
import { Decorators } from './decorators/useDecorators'
import { EditorContext } from './EditorContext'
import TextAreaInput from './EditorInput'
import { IProcessorFabric, IProcessorState, ISlice, ITextProcessor } from './processor/constants'
import { isBoldToggleEvent, isItalicToggleEvent, isUnderlineToggleEvent } from './processor/helpers'
import { InputElement } from './types'
import { isHistoryEvent } from './useHistory'

import cm from './Editor.module.css'

export interface IEditorProps {
  className?: string
  createProcessor: IProcessorFabric
  processor: ITextProcessor
  'data-test-id'?: string
  decorators: Decorators
  disabled?: boolean
  multiline?: boolean
  name?: string
  noWrap?: boolean
  onBlur?: React.FocusEventHandler<InputElement>
  onFocus?: React.FocusEventHandler<InputElement>
  onKeyDown?: React.KeyboardEventHandler<InputElement>
  onPasteImage?: (imageFiles: File[]) => void
  placeholder?: string | React.ReactNode
  readonly?: boolean
  rows?: number
  useContentEditable?: boolean
  exportString: (processorState: IProcessorState) => string
  parseString: (text: string) => { text: string; slices: ISlice[] }
  sliceComponent: SliceFC
  sortSlices: SlicesOrderComparator
}

export interface IEditorRef {
  inputRef: React.MutableRefObject<InputElement | null>

  blur: () => void
  focus: () => void
}

const Editor: React.ForwardRefRenderFunction<IEditorRef, IEditorProps> = (props, ref) => {
  const {
    name,
    className,
    processor,
    createProcessor,
    exportString,
    parseString,
    decorators,
    onBlur,
    onFocus,
    onKeyDown,
    onPasteImage,
    sliceComponent,
    sortSlices,
    placeholder,
    rows,
    noWrap,
    useContentEditable = false,
    disabled = false,
    readonly = false,
    multiline = true,
  } = props
  const inputRef = React.useRef<InputElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  const { edit, undo, redo } = React.useContext(EditorContext)

  const instance = {
    inputRef,

    blur: () => {
      inputRef.current?.blur()
    },
    focus: () => {
      inputRef.current?.focus()
    },
  }

  React.useImperativeHandle(ref, () => instance)

  const handleSelect = (start: number, end: number) => {
    const selection = processor.getSelection()
    if (start === selection.start && end === selection.end) {
      return
    }

    processor.select(start, end)
    edit(processor.getState())
  }

  const handleInput = (value: string, selectionIndex: number) => {
    const slices = decorators.getList()
    processor.input(value, selectionIndex, slices)
    edit(processor.getState())
  }

  const handleKeyDown = (event: React.KeyboardEvent<InputElement>) => {
    if (onKeyDown) {
      onKeyDown(event)
      if (event.defaultPrevented) {
        return
      }
    }

    if (event.keyCode === 13) {
      event.preventDefault()
      const s = processor.getSelection()
      if (s.length > 0) {
        processor.delete(s.start, s.length)
      }
      processor.insert(s.start, '\n')
      edit(processor.getState())
    }

    if (useContentEditable) {
      if (isBoldToggleEvent(event)) {
        decorators.toggle({ type: SliceType.BOLD })
      }
      if (isItalicToggleEvent(event)) {
        decorators.toggle({ type: SliceType.ITALIC })
      }
      if (isUnderlineToggleEvent(event)) {
        decorators.toggle({ type: SliceType.UNDERLINE })
      }
    }

    if (isHistoryEvent(event)) {
      event.preventDefault()
      event.shiftKey ? redo() : undo()
    }
  }

  const handleCopy = (event: React.ClipboardEvent<InputElement>) => {
    event.preventDefault()
    const copyProcessor = createProcessor(processor.getState())
    const selection = copyProcessor.getSelection()
    copyProcessor.delete(selection.end, copyProcessor.getText().length - selection.end)
    copyProcessor.delete(0, selection.start)
    const clipboardText = exportString(copyProcessor.getState())

    event.clipboardData.setData('text/plain', clipboardText)
  }

  const handleCut = (event: React.ClipboardEvent<InputElement>) => {
    handleCopy(event)
    if (readonly) {
      return
    }
    processor.paste('')
    edit(processor.getState())
  }

  const handlePaste = (event: React.ClipboardEvent<InputElement>) => {
    if (readonly) {
      return
    }
    const text = event.clipboardData.getData('text')
    if (text) {
      event.preventDefault()
      const parseResult = parseString(text)
      processor.paste(parseResult.text, parseResult.slices)
      edit(processor.getState())
      return
    }

    if (!onPasteImage) {
      return
    }
    const imageFiles = getImageFiles(event.clipboardData)
    if (imageFiles.length) {
      onPasteImage(imageFiles)
    }
  }

  const wrapperClassName = React.useMemo(
    () =>
      cx(className, 'p-relative notranslate', {
        [cm.noWrap]: noWrap,
        [cm.singleLine]: !multiline,
      }),
    [className, multiline, noWrap],
  )

  const Input = useContentEditable ? ContentEditableInput : TextAreaInput

  const text = processor.getText()

  return (
    <div className={wrapperClassName}>
      {useContentEditable && !text && <div style={{ position: 'absolute' }}>{placeholder}</div>}

      <div translate="no" ref={contentRef} className={cm.content}>
        <EditorContent
          processor={processor}
          sliceComponent={sliceComponent}
          sortSlices={sortSlices}
        />
      </div>
      <Input
        ref={inputRef}
        contentRef={contentRef}
        className={cm.input}
        data-test-id={props['data-test-id']}
        disabled={disabled}
        name={name}
        onChange={handleInput}
        onCopy={handleCopy}
        onCut={handleCut}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onSelect={handleSelect}
        placeholder={(placeholder && typeof placeholder === 'string' && placeholder) || undefined}
        readOnly={readonly}
        rows={rows}
        selection={processor.getSelection()}
        value={processor.getText()}
        multiline={multiline}
        noWrap={noWrap}
      />
    </div>
  )
}

export default React.forwardRef(Editor)

function getImageFiles(clipboardData: DataTransfer): File[] {
  const { items } = clipboardData
  const imageFiles: File[] = []

  // IOS Safari has no items in clipboardData
  if (!items) {
    return imageFiles
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file !== null && file.type.includes('image')) {
        imageFiles.push(file)
      }
    }
  }
  return imageFiles
}
